import { useMemo } from 'react';
import { components as selectComponents, OptionProps } from 'react-select';
import { createUseStyles } from 'react-jss';

import { Select, SelectProps } from '@app/form';
import { Theme } from '@app/theme';

import {
  Calendar,
  Feature,
  useCalendar,
  useCapabilities,
  useIsAdmin,
  VideoCallProvider,
} from '@app/api';
import { LockFeatureChip } from '@app/core';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

const useStyles = createUseStyles((theme: Theme) => ({
  optionContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.marginBase,
  },
  optionFirstLine: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase,
  },
  icon: {
    fontSize: theme.iconSize.regular,
    color: theme.colors.inkTertiary,
    marginRight: theme.marginBase,
  },
  text: {
    ...theme.fonts.caption,
    color: theme.colors.inkSecondary,
  },
  profileName: {
    ...theme.fonts.caption,
    color: theme.colors.inkTertiary,
  },
  secondaryText: {
    ...theme.fonts.caption,
    color: theme.colors.inkTertiary,
    textAlign: 'right',
  },
}));

const components = {
  Option: (props: OptionProps<any, boolean>) => {
    const classes = useStyles();

    return (
      <selectComponents.Option {...props}>
        <div className={classes.optionContainer}>
          <div className={classes.optionFirstLine}>
            <i className={classNames(props.data.icon, classes.icon)} />
            <div className={classes.text}>{props.children}</div>
          </div>
          <LockFeatureChip
            feature={props.data.feature}
            planId={props.data.planId}
          />
        </div>
      </selectComponents.Option>
    );
  },
};

const isOptionDisabled = (option: any) => !!option?.disabled;

/**
 * The integrated visio is created by the calendar itself, while Zoom and 8x8 are only created through Cronofy.
 */
export const isVideoCallProviderAvailableForCalendar = (
  provider: VideoCallProvider | undefined,
  calendar: Calendar | undefined,
): boolean => {
  switch (provider) {
    case VideoCallProvider.INTEGRATED:
      return !!calendar?.integratedConferencingServiceAvailable;
    case VideoCallProvider.ZOOM:
    case VideoCallProvider.CRONOFY:
      return !!calendar?.cronofyConferencingAvailable;
    default:
      return true;
  }
};

export const VideoConferencingProviderSelect = ({
  calendarId,
  planId,
  organizationId,
  ...props
}: SelectProps & {
  calendarId?: string;
  planId?: string;
  organizationId?: string;
}) => {
  const { t } = useTranslation();
  const { data: calendar } = useCalendar(calendarId);
  const capabilities = useCapabilities(planId, organizationId);
  const isAdmin = useIsAdmin();

  const options = useMemo(
    () => [
      {
        label: t('editSession.settings.videoCallProviderOptions.custom'),
        value: VideoCallProvider.CUSTOM,
        disabled: false,
        icon: 'ri-link-m',
        feature: null,
        planId: planId,
      },
      {
        label: t('editSession.settings.videoCallProviderOptions.integrated', {
          context: calendar?.provider,
        }),
        value: VideoCallProvider.INTEGRATED,
        disabled:
          !isVideoCallProviderAvailableForCalendar(
            VideoCallProvider.INTEGRATED,
            calendar,
          ) ||
          (!isAdmin && !capabilities?.conferencingIntegrationIntegrated),
        icon:
          calendar?.provider === 'google'
            ? 'ri-google-fill'
            : calendar?.provider === 'exchange' ||
              calendar?.provider === 'microsoft'
            ? 'ri-microsoft-fill'
            : 'ri-vidicon-line',
        feature: Feature.CONFERENCING_INTEGRATION_INTEGRATED,
        planId: planId,
      },
      {
        label: t('editSession.settings.videoCallProviderOptions.google_meet'),
        value: VideoCallProvider.GOOGLE_MEET,
        disabled: true,
        icon: 'ri-google-fill',
        feature: null,
        planId: planId,
      },
      {
        label: t(
          `editSession.settings.videoCallProviderOptions.${
            !calendar ? 'zoom_calendar' : 'zoom'
          }`,
        ),
        value: VideoCallProvider.ZOOM,
        icon: 'ri-vidicon-line',
        disabled: !isVideoCallProviderAvailableForCalendar(
          VideoCallProvider.ZOOM,
          calendar,
        ),
        feature: Feature.CONFERENCING_INTEGRATION_ZOOM,
        planId: planId,
      },
      {
        label: t('editSession.settings.videoCallProviderOptions.8x8'),
        value: VideoCallProvider.CRONOFY,
        disabled:
          !isVideoCallProviderAvailableForCalendar(
            VideoCallProvider.CRONOFY,
            calendar,
          ) ||
          (!isAdmin && !capabilities?.conferencingIntegration8x8),
        icon: 'ri-vidicon-line',
        feature: Feature.CONFERENCING_INTEGRATION_8X8,
        planId: planId,
      },
      ...(isAdmin
        ? [
            {
              label: t('editSession.settings.videoCallProviderOptions.buzz'),
              value: VideoCallProvider.BUZZ,
              icon: 'ri-flashlight-line',
              planId: planId,
            },
          ]
        : []),
    ],
    [calendar, t, planId, capabilities, isAdmin],
  );

  return (
    <Select
      {...props}
      components={components as any}
      options={options}
      isOptionDisabled={isOptionDisabled}
    />
  );
};
